import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

export default function ManualScraperVerify() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [status, setStatus] = useState<"verifying" | "ok" | "error">(
    "verifying"
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function verify() {
      if (!token) {
        setStatus("error");
        setError("Missing sign-in token.");
        return;
      }
      const res = await api<{ ok: true }>("/manual-scraper/verify", {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      if (cancelled) return;
      if (!res.ok) {
        setStatus("error");
        setError(res.error ?? "Sign-in link is invalid or expired.");
        return;
      }
      setStatus("ok");
      setTimeout(() => navigate("/manual-scrape", { replace: true }), 500);
    }
    void verify();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 dark:bg-slate-950">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
        {status === "verifying" && (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-slate-400" />
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
              Signing you in…
            </p>
          </>
        )}
        {status === "ok" && (
          <>
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600" />
            <p className="mt-3 text-sm text-slate-700 dark:text-slate-300">
              Signed in — redirecting…
            </p>
          </>
        )}
        {status === "error" && (
          <>
            <AlertTriangle className="mx-auto h-8 w-8 text-red-500" />
            <h1 className="mt-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
              Couldn't sign you in
            </h1>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              {error}
            </p>
            <Link
              to="/manual-scrape/login"
              className="mt-5 inline-block rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
            >
              Request a new link
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
